import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AtSign, Instagram, Play, Video, Youtube } from "lucide-react";
import { fetchClips, fetchProjects } from "../lib/api";
import Reveal from "../components/Reveal";

// Preferred chip order. "AI Experiments" is a special bucket that pulls
// from Clips instead of Projects (Threads-sourced, ongoing work).
const PROJECT_CATEGORY_ORDER = [
    "Narrative",
    "Commercial",
    "Comedy",
    "Short Film",
    "Documentary",
    "Music Video",
    "Other",
];
const AI_CATEGORY = "AI Experiments";

const SOURCE_ICON = {
    threads: AtSign,
    instagram: Instagram,
    youtube: Youtube,
    vimeo: Video,
    upload: Play,
};
const sourceLabel = (s) =>
    ({
        threads: "Threads",
        instagram: "Instagram",
        youtube: "YouTube",
        vimeo: "Vimeo",
        upload: "Studio",
    })[s] || "Studio";

const Portfolio = () => {
    const [projects, setProjects] = useState([]);
    const [aiClips, setAiClips] = useState([]);
    const [active, setActive] = useState("All");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetchProjects(),
            fetchClips({ category: AI_CATEGORY, limit: 24 }),
        ])
            .then(([p, c]) => {
                setProjects(p);
                setAiClips(c);
            })
            .catch(() => {
                setProjects([]);
                setAiClips([]);
            })
            .finally(() => setLoading(false));
    }, []);

    // Chip list — built from real data. AI Experiments appears iff any clip
    // exists; project categories appear iff a project uses them.
    const categories = useMemo(() => {
        const present = new Set(projects.map((p) => p.category).filter(Boolean));
        const ordered = PROJECT_CATEGORY_ORDER.filter((c) => present.has(c));
        const extras = [...present].filter(
            (c) => !PROJECT_CATEGORY_ORDER.includes(c),
        );
        const list = ["All", ...ordered, ...extras];
        if (aiClips.length > 0) list.push(AI_CATEGORY);
        return list;
    }, [projects, aiClips]);

    const filteredProjects = useMemo(() => {
        if (active === "All") return projects;
        if (active === AI_CATEGORY) return [];
        return projects.filter((p) => p.category === active);
    }, [projects, active]);

    const showAI = active === AI_CATEGORY;

    return (
        <main
            data-testid="portfolio-page"
            className="pt-32 sm:pt-40 pb-32 mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14"
        >
            <Reveal>
                <p className="text-label text-muted-foreground mb-6">
                    Selected Work
                </p>
                <h1 className="font-serif-display text-5xl sm:text-7xl tracking-tighter leading-[1.02] max-w-4xl">
                    A catalogue of{" "}
                    <span className="italic text-muted-foreground">
                        light, time,
                    </span>{" "}
                    and patience.
                </h1>
            </Reveal>

            <Reveal delay={150}>
                <div
                    data-testid="portfolio-filters"
                    className="mt-16 flex flex-wrap gap-x-8 gap-y-3 border-y border-border py-6"
                >
                    {categories.map((c) => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setActive(c)}
                            data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`text-label transition-colors duration-500 ${
                                active === c
                                    ? "text-foreground"
                                    : "text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </Reveal>

            {loading ? (
                <div className="mt-20 text-muted-foreground text-sm tracking-wider">
                    Loading…
                </div>
            ) : showAI ? (
                <AIExperimentsGrid clips={aiClips} />
            ) : filteredProjects.length === 0 ? (
                <div
                    data-testid="portfolio-empty"
                    className="mt-32 text-center text-muted-foreground font-serif-display text-2xl"
                >
                    No projects in this category — yet.
                </div>
            ) : (
                <div
                    data-testid="portfolio-grid"
                    className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-24"
                >
                    {filteredProjects.map((p, i) => (
                        <Reveal
                            key={p.id}
                            delay={(i % 2) * 120}
                            className={
                                i % 3 === 0
                                    ? "md:mt-0"
                                    : i % 3 === 1
                                      ? "md:mt-20"
                                      : ""
                            }
                        >
                            <Link
                                to={`/work/${p.id}`}
                                data-testid={`portfolio-card-${p.id}`}
                                className="block group"
                            >
                                <div className="overflow-hidden bg-muted aspect-[4/3]">
                                    <img
                                        src={p.thumbnail_url}
                                        alt={p.title}
                                        loading="lazy"
                                        style={
                                            p.thumbnail_position
                                                ? {
                                                      objectPosition:
                                                          p.thumbnail_position,
                                                  }
                                                : undefined
                                        }
                                        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                                    />
                                </div>
                                <div className="mt-5 flex items-baseline justify-between gap-6">
                                    <h3 className="font-serif-display text-2xl tracking-tight">
                                        {p.title}
                                    </h3>
                                    <span className="text-label text-muted-foreground shrink-0">
                                        {p.year}
                                    </span>
                                </div>
                                <p className="mt-2 text-sm text-muted-foreground tracking-wide">
                                    {p.role} — {p.category}
                                </p>
                                {p.awards?.length > 0 && (
                                    <p className="mt-2 text-xs text-foreground/70 italic">
                                        {p.awards[0]}
                                    </p>
                                )}
                            </Link>
                        </Reveal>
                    ))}
                </div>
            )}
        </main>
    );
};

const AIExperimentsGrid = ({ clips }) => {
    if (!clips.length) {
        return (
            <div
                data-testid="portfolio-ai-empty"
                className="mt-32 text-center text-muted-foreground font-serif-display text-2xl"
            >
                Experiments are being archived — check back soon.
            </div>
        );
    }
    return (
        <div data-testid="portfolio-ai-section" className="mt-20">
            <Reveal>
                <p className="max-w-2xl text-base text-foreground/70 leading-relaxed mb-12">
                    Short generative pieces and visual prompts. Posted as they
                    happen — most originate on Threads.
                </p>
            </Reveal>
            <div
                data-testid="portfolio-ai-grid"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16"
            >
                {clips.map((c, i) => {
                    const Icon = SOURCE_ICON[c.source] || Play;
                    return (
                        <Reveal key={c.id} delay={(i % 3) * 100}>
                            <a
                                href={c.embed_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`portfolio-ai-card-${c.id}`}
                                className="block group"
                            >
                                <div className="relative overflow-hidden bg-muted aspect-[4/5]">
                                    <img
                                        src={c.poster_url || c.thumbnail_url}
                                        alt={c.title || "AI experiment"}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                                    />
                                    <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-foreground/80 bg-background/70 backdrop-blur px-2 py-1 border border-border/60">
                                        <Icon size={11} strokeWidth={1.5} />
                                        {sourceLabel(c.source)}
                                    </span>
                                </div>
                                <div className="mt-5 flex items-baseline justify-between gap-6">
                                    <h3 className="font-serif-display text-xl tracking-tight">
                                        {c.title || "Untitled"}
                                    </h3>
                                    {c.year && (
                                        <span className="text-label text-muted-foreground shrink-0">
                                            {c.year}
                                        </span>
                                    )}
                                </div>
                                {c.caption && (
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">
                                        {c.caption}
                                    </p>
                                )}
                            </a>
                        </Reveal>
                    );
                })}
            </div>
        </div>
    );
};

export default Portfolio;
